import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Loader2, Mail, MapPin, Send } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CLIENT } from "@/data/content";
import { COUNTRY_CODES } from "@/data/countryCodes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

const inputClass =
  "w-full rounded-lg border border-white/10 bg-[#121212] px-4 py-3.5 text-sm text-neutral-100 placeholder:text-neutral-600 transition-[border-color,box-shadow] duration-300 focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/20";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [phoneCode, setPhoneCode] = useState("+971");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Your name is required";
    if (!form.email.trim()) er.email = "Your email is required";
    else if (!EMAIL_RE.test(form.email.trim())) er.email = "Enter a valid email address";
    if (!form.subject.trim()) er.subject = "A subject is required";
    if (!form.message.trim()) er.message = "Tell me about your show";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() ? `${phoneCode} ${form.phone.trim()}` : "",
        company: form.company.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setStatus("success");
      toast.success("Message sent — I'll be in touch soon.");
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again or email me directly.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-white/5 bg-[#0A0A0A] py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-5 lg:px-10">
        <div className="lg:col-span-2">
          <SectionHeading
            label="Contact"
            title="Let's make it"
            accent="sound right"
            description="Tell me about the show, the venue and the date — I'll come back with a plan for the audio."
            testId="contact-heading"
          />
          <Reveal delay={0.15}>
            <div className="space-y-5">
              <a
                href={`mailto:${CLIENT.email}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-[border-color] duration-300 hover:border-electric/50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-electric/30 bg-electric/10 text-electric">
                  <Mail size={20} aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Email</span>
                  <span className="block text-sm font-semibold text-neutral-200 group-hover:text-electric">{CLIENT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="contact-location-card">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-electric/30 bg-electric/10 text-electric">
                  <MapPin size={20} aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Based in</span>
                  <span className="block text-sm font-semibold text-neutral-200">Dubai, UAE & India — available for regional & international shows</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          {status === "success" ? (
            <div className="flex h-full flex-col items-start justify-center rounded-2xl border border-electric/40 bg-electric/[0.06] p-10" data-testid="contact-success-panel">
              <CheckCircle2 size={44} className="text-electric" aria-hidden="true" />
              <h3 className="mt-6 font-display text-3xl font-semibold uppercase tracking-tight text-neutral-100">Message received</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-neutral-400">
                Thanks, {form.name.split(" ")[0]} — your enquiry is on its way to my inbox, and a confirmation
                is heading to yours. I'll get back to you within 24–48 hours.
              </p>
              <button
                type="button"
                data-testid="contact-send-another-button"
                onClick={() => { setForm(EMPTY); setStatus("idle"); }}
                className="mt-8 rounded-full border border-white/20 px-6 py-3 text-xs font-extrabold uppercase tracking-[0.15em] text-neutral-200 transition-[border-color,color] duration-300 hover:border-electric hover:text-electric"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="contact-form" className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" type="text" value={form.name} onChange={update("name")} placeholder="Your full name" className={inputClass} aria-invalid={!!errors.name} />
                  {errors.name && <p className="mt-1.5 text-xs text-scarlet" data-testid="contact-name-error">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputClass} aria-invalid={!!errors.email} />
                  {errors.email && <p className="mt-1.5 text-xs text-scarlet" data-testid="contact-email-error">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Phone</label>
                  <div className="flex gap-2">
                    <select
                      id="contact-phone-code"
                      data-testid="contact-phone-code-select"
                      aria-label="Country code"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                      className="w-24 shrink-0 cursor-pointer appearance-none truncate rounded-lg border border-white/10 bg-[#121212] px-3 py-3.5 text-sm text-neutral-100 transition-[border-color,box-shadow] duration-300 focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/20"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={`${c.code}-${c.label}`} value={c.code} className="bg-[#121212] text-neutral-100">
                          {c.code} {c.label}
                        </option>
                      ))}
                    </select>
                    <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={update("phone")} placeholder="50 123 4567" className={`${inputClass} min-w-0 flex-1`} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-company" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Company</label>
                  <input id="contact-company" data-testid="contact-company-input" type="text" value={form.company} onChange={update("company")} placeholder="Production company / venue" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-subject" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Subject *</label>
                  <input id="contact-subject" data-testid="contact-subject-input" type="text" value={form.subject} onChange={update("subject")} placeholder="e.g. FOH engineer for arena show — March 2026" className={inputClass} aria-invalid={!!errors.subject} />
                  {errors.subject && <p className="mt-1.5 text-xs text-scarlet" data-testid="contact-subject-error">{errors.subject}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">Message *</label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows={5} value={form.message} onChange={update("message")} placeholder="Show dates, venue, expected audience size, what you need engineered..." className={`${inputClass} resize-y`} aria-invalid={!!errors.message} />
                  {errors.message && <p className="mt-1.5 text-xs text-scarlet" data-testid="contact-message-error">{errors.message}</p>}
                </div>
              </div>

              {status === "error" && (
                <div className="mt-5 flex items-center gap-3 rounded-lg border border-scarlet/40 bg-scarlet/10 px-4 py-3 text-sm text-neutral-200" data-testid="contact-error-banner" role="alert">
                  <AlertCircle size={18} className="shrink-0 text-scarlet" aria-hidden="true" />
                  The message couldn't be sent. Please try again — or email me directly at {CLIENT.email}.
                </div>
              )}

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={status === "submitting"}
                className="mt-7 inline-flex items-center gap-3 rounded-full bg-electric px-9 py-4 text-xs font-extrabold uppercase tracking-[0.15em] text-[#050505] transition-[transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#ffa733] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send size={15} aria-hidden="true" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
